/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commerical BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-09-09 14:55:08
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Card, TextStyle, Button, FormLayout, InlineError, Link } from '@shopify/polaris';
import { Field, reduxForm, getFormValues } from 'redux-form';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import { URL } from 'src/constants/links';
import history from 'src/routes/history';
import { routes } from 'src/routes';
import { RenderSelect2 } from 'src/components/views/common/forms/render-select-2';
import ConfirmSwitchLayoutModal from 'src/components/views/switch-layout/switch-layout-form/confirm-switch-layout-modal';
import ThemeErrorModal from 'src/components/containers/common/theme-error-modal';
import { startSABLoading, stopSABLoading } from 'src/integrations/shopify-app-bridge/loading';
import * as themeSetupAction from 'actions/theme-setup-actions';

type Props = {
  theme: Object,
  getThemesLoading: boolean,
  themes: Array<string>,
  isTrialTheme: boolean,
  inputThemeName: string,
  outputThemeName: string,
  isOS20: boolean,
  themeSetupV2: boolean,
  checkThemeLoading: boolean,
  setupThemeLoading: boolean,
  setupThemeSuccess: boolean,
  setupThemeError: any,
  getThemes: () => void,
  switchLayout: (data: { theme: string, layout: string }) => void,
  unmountComponent: () => void
};

type ComposedProps = Props & WithI18nProps & FormProps;

type State = {
  activeModalConfirmSetup: boolean,
  activeModalError: boolean,
  dataSetup: Object
};

class SwitchLayout extends Component<ComposedProps, State> {
  state = {
    activeModalConfirmSetup: false,
    activeModalError: false,
    dataSetup: {}
  };

  componentDidMount() {
    const { getThemes } = this.props;

    getThemes();
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.getThemesLoading === true && this.props.getThemesLoading === false) {
      startSABLoading();
    }

    if (nextProps.getThemesLoading === false && this.props.getThemesLoading === true) {
      stopSABLoading();
    }

    if (this.props.setupThemeLoading === true && nextProps.setupThemeLoading === false) {
      if (nextProps.setupThemeError !== undefined) {
        this.setState({ activeModalError: true });
      } else if (nextProps.setupThemeSuccess) {
        history.push({
          pathname: routes.switchLayoutSuccess.view()
        });
      }
    }
  }

  componentWillUnmount() {
    const { unmountComponent } = this.props;
    unmountComponent();
  }

  render() {
    const { dataSetup, activeModalConfirmSetup, activeModalError } = this.state;
    const {
      i18n,
      theme,
      themes,
      handleSubmit,
      setupThemeLoading,
      isTrialTheme,
      inputThemeName,
      outputThemeName,
      isOS20,
      pristine,
      submitting,
      checkThemeLoading,
      getThemesLoading,
      themeSetupV2
    } = this.props;

    const themesList = themes
      ? themes.map(item => ({
          value: item.id,
          label: `${item.name}${
            item.published
              ? i18n.translate('AdminHTML.SwitchLayout.SwitchLayout.suffixThemeLive')
              : ''
          }`,
          published: item.published,
          layout: item.layout
        }))
      : [];

    // Theme setup v1, select theme OS 2.0
    const warningOS20 =
      !isTrialTheme && !themeSetupV2 && isOS20
        ? i18n.translate('AdminHTML.ThemeSetup.ThemeSetup.warningOS20', {
            nt_break_line: <br />
          })
        : '';

    const warning = !isTrialTheme &&
      inputThemeName !== 'default' &&
      outputThemeName === 'default' && (
        <span>
          {warningOS20}
          {i18n.translate('AdminHTML.SwitchLayout.SwitchLayout.warning', {
            nt_break_line: <br />
          })}
        </span>
      );

    const info = !isTrialTheme &&
      inputThemeName === outputThemeName &&
      !_.isEmpty(inputThemeName) && (
        <TextStyle variation="positive">
          {warningOS20}
          {i18n.translate('AdminHTML.SwitchLayout.SwitchLayout.info')}
        </TextStyle>
      );

    return (
      <Card>
        <Card.Section>
          <div className="BC-Page BC-Page__Text">
            <TextStyle>
              {i18n.translate('AdminHTML.SwitchLayout.SwitchLayout.recommend', {
                t1: (
                  <TextStyle variation="strong">
                    {i18n.translate('AdminHTML.SwitchLayout.SwitchLayout.recommend_t1')}
                  </TextStyle>
                ),
                t2: (
                  <Link external url={URL['DuplicatingThemesDoc']}>
                    {i18n.translate('AdminHTML.SwitchLayout.SwitchLayout.recommend_t2')}
                  </Link>
                )
              })}
            </TextStyle>
          </div>
        </Card.Section>
        <Card.Section>
          <form className="BC-Layout-Switch_Form">
            <FormLayout>
              <div className="BC-Layout-Switch_Form-Item">
                <div className="title">
                  <TextStyle variation="strong">
                    {i18n.translate('AdminHTML.SwitchLayout.SwitchLayout.selectTheme')}
                  </TextStyle>
                </div>
                <Field
                  name="theme"
                  component={RenderSelect2}
                  options={themesList}
                  placeholder={i18n.translate(
                    'AdminHTML.SwitchLayout.SwitchLayout.theme_Placeholder'
                  )}
                  placeholderEmpty={i18n.translate(
                    'AdminHTML.SwitchLayout.SwitchLayout.theme_PlaceholderEmpty'
                  )}
                  isDisabled={_.isEmpty(themesList) || checkThemeLoading || setupThemeLoading}
                />
                {warning && (
                  <div className="inline-warning">
                    <InlineError message={warning} />
                  </div>
                )}
                {info && <div className="inline-info">{info}</div>}
              </div>

              {theme && theme.layout && (
                <TextStyle variation="positive">
                  <div className="inline-info-blue">
                    {i18n.translate('AdminHTML.SwitchLayout.SwitchLayout.themeSwitchInfo', {
                      t1: (
                        <TextStyle variation="strong">
                          {theme.layout === '-v'
                            ? i18n.translate(
                                'AdminHTML.SwitchLayout.SwitchLayout.themeSwitchInfo_t_themeLeft'
                              )
                            : i18n.translate(
                                'AdminHTML.SwitchLayout.SwitchLayout.themeSwitchInfo_t_themeTop'
                              )}
                        </TextStyle>
                      ),
                      t2: (
                        <TextStyle variation="strong">
                          {theme.layout === '-v'
                            ? i18n.translate(
                                'AdminHTML.SwitchLayout.SwitchLayout.themeSwitchInfo_t_themeTop'
                              )
                            : i18n.translate(
                                'AdminHTML.SwitchLayout.SwitchLayout.themeSwitchInfo_t_themeLeft'
                              )}
                        </TextStyle>
                      )
                    })}
                  </div>
                </TextStyle>
              )}

              <div className="BC-Layout-Switch_Form-Item-Next">
                <Button
                  primary
                  onClick={handleSubmit(this.handleSwitchLayout.bind(this))}
                  loading={setupThemeLoading}
                  disabled={
                    pristine ||
                    submitting ||
                    checkThemeLoading ||
                    getThemesLoading ||
                    isTrialTheme ||
                    _.isEmpty(theme)
                  }>
                  {i18n.translate('AdminHTML.Common.Action.runSetup')}
                </Button>
                {setupThemeLoading && (
                  <span style={{ color: 'var(--p-interactive)', marginLeft: '16px' }}>
                    {i18n.translate('AdminHTML.SwitchLayout.SwitchLayout.setupLoading')}
                  </span>
                )}
              </div>
            </FormLayout>
          </form>
        </Card.Section>
        <ConfirmSwitchLayoutModal
          data={dataSetup}
          active={activeModalConfirmSetup}
          handleChange={() => this.toggleChangeState('activeModalConfirmSetup')}
          handleContinue={this.switchLayout}
        />
        <ThemeErrorModal
          active={activeModalError}
          handleChange={() => this.toggleChangeState('activeModalError')}
          handleSubmitEmail={this.handleSubmitEmail}
        />
      </Card>
    );
  }

  // Toggle change state
  toggleChangeState = (id: string) => {
    const value = this.state[id];
    this.setState({ [id]: !value });
  };

  //Handle submit
  handleSwitchLayout = data => {
    const { theme } = data;
    if (theme.published) {
      this.setState({ activeModalConfirmSetup: true, dataSetup: data });
    } else {
      this.switchLayout(data);
    }
  };

  // Run switch layout
  switchLayout = data => {
    const { switchLayout } = this.props;
    const { activeModalConfirmSetup } = this.state;

    // Handle if the confirm modal is active
    if (activeModalConfirmSetup) {
      this.setState({ activeModalConfirmSetup: false });
    }

    // Run switch layout
    let prettyData = {
      themeId: data.theme.value,
      layout: data.theme.layout === '-h' ? 'v' : 'h'
    };
    switchLayout(prettyData);
  };
}

const asyncValidate = async (values, dispatch, props) => {
  const value = _.get(values, 'theme.value', '');
  if (value) {
    dispatch(themeSetupAction.checkTheme(value, props.i18n));
  }
};

const SwitchLayoutForm = reduxForm({
  form: 'SwitchLayout', // a unique identifier for this form
  enableReinitialize: true,
  asyncValidate,
  asyncChangeFields: ['theme'],
  shouldAsyncValidate: params => {
    return params.trigger === 'change' && params.syncValidationPasses; // do not async validate on submit
  }
})(SwitchLayout);

const mapStateToProps = (state, props) => {
  const { themeSetup = {}, oauth = {} } = state;
  const formValues = getFormValues('SwitchLayout')(state);
  const themeSetupV2 = _.get(oauth, 'tenantApp.adminAppFeatures.themeSetupV2', false);

  return {
    theme: formValues && formValues.theme ? formValues.theme : {},
    getThemesLoading: _.get(themeSetup, 'getThemesLoading', false),
    themes: _.get(themeSetup, 'themes', []),
    isTrialTheme: _.get(themeSetup, 'isTrialTheme', false),
    inputThemeName: _.get(themeSetup, 'inputThemeName', ''),
    outputThemeName: _.get(themeSetup, 'outputThemeName', ''),
    isOS20: _.get(themeSetup, 'isOS20', false),
    themeSetupV2,
    checkThemeLoading: _.get(themeSetup, 'checkThemeLoading', false),
    setupThemeLoading: _.get(themeSetup, 'setupThemeLoading', false),
    setupThemeSuccess: _.get(themeSetup, 'setupThemeSuccess', false),
    setupThemeError: _.get(themeSetup, 'setupThemeError', undefined)
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  getThemes: () => dispatch(themeSetupAction.getThemes(ownProps.i18n)),
  switchLayout: (data: { theme: string, layout: string }) =>
    dispatch(themeSetupAction.setupTheme(data, undefined, ownProps.i18n)),
  unmountComponent: () => dispatch(themeSetupAction.unmountComponent())
});

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(SwitchLayoutForm));
